/**
 * ServiceOrderSummaryTab - Aba Resumo da OS em cartões premium.
 *
 * Cartões: Cliente e Veículo, Serviço, Observações e Endereço de Prestação.
 *
 * RELEASE: 2026-10-13 v1.2.0 - Coordenadas do local e cartão Chegada ao local (distância, precisão, área).
 * RELEASE: 2026-10-10 v1.1.0 - Telefone mascarado e CPF/CNPJ no resumo do cliente.
 * RELEASE: 2026-10-09 v1.0.0 - Versão inicial (padronização com Tarefas).
 */
import type { ReactNode } from 'react';
import { Car, LocateFixed, MapPin, MessageSquareText, Phone, User, Wrench } from 'lucide-react';
import { useServiceOrderDestination } from '@/hooks/useServiceOrderDestination';
import { formatCoordinates } from '@/lib/serviceOrderAddress';
import { Button } from '@/components/ui/button';
import { VehiclePlateChip } from '@/components/vehicles/VehiclePlateChip';
import { DetailsContentCard } from '@/components/shared/details-modal/DetailsModalPrimitives';
import { GOOGLE_MAPS_URL_RE } from '@/lib/serviceOrderAddress';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import type { ServiceOrderDetails } from '@/hooks/useServiceOrderDetails';

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

interface Props {
  order: ServiceOrderDetails;
  /** Barra lateral embutida no celular */
  mobileSidebar?: ReactNode;
}

export function ServiceOrderSummaryTab({ order: o, mobileSidebar }: Props) {
  const dest = useServiceOrderDestination(o.id).data;
  const arrival = dest?.arrival;
  const coords = formatCoordinates(dest?.lat, dest?.lng);

  const address = [o.address_street, o.address_number, o.address_complement, o.address_district,
    [o.address_city, o.address_state].filter(Boolean).join('/'), o.address_cep].filter(Boolean).join(', ');
  const mapsOk = !!o.address_maps_url && GOOGLE_MAPS_URL_RE.test(o.address_maps_url);
  const hasLocation = !!(address || o.address_reference || o.address_phone || o.address_contact_name || mapsOk || coords);

  return (
    <div className="space-y-4">
      <DetailsContentCard icon={User} title="Cliente e Veículo">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <Info label="Cliente">
            <span className="flex flex-col">
              <span>{o.customer_name || '—'}</span>
              {o.customer_document && (
                <span className="text-xs text-muted-foreground">{formatDocumentForDisplay(o.customer_document)}</span>
              )}
            </span>
          </Info>
          <Info label="Telefone do Cliente">
            {o.customer_phone ? <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{formatPhoneForDisplay(o.customer_phone)}</span> : '—'}
          </Info>
          <Info label="Veículo">
            {o.vehicle_plate ? (
              <span className="inline-flex flex-wrap items-center gap-2"><VehiclePlateChip plate={o.vehicle_plate} />
                <span className="text-muted-foreground">{o.vehicle_label}</span></span>
            ) : <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Car className="h-3.5 w-3.5" />Sem veículo</span>}
          </Info>
          <Info label="Detalhes do Veículo">{[o.vehicle_color, o.vehicle_year].filter(Boolean).join(' · ') || '—'}</Info>
        </div>
      </DetailsContentCard>

      <DetailsContentCard icon={Wrench} title="Serviço">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Info label="Tipo de Serviço"><span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: o.service_type_color }} />{o.service_type_name}</span></Info>
          <Info label="Prestador">{o.provider_name || '—'}</Info>
          <Info label="Duração Estimada">{o.service_type_duration ? `${o.service_type_duration} minutos` : '—'}</Info>
        </div>
      </DetailsContentCard>

      <DetailsContentCard icon={MessageSquareText} title="Observações">
        {o.description ? <p className="whitespace-pre-wrap text-sm leading-relaxed">{o.description}</p> : <p className="text-sm italic text-muted-foreground">Sem observações.</p>}
      </DetailsContentCard>

      <DetailsContentCard icon={MapPin} title="Endereço de Prestação"
        action={mapsOk ? (
          <Button asChild size="sm" variant="outline" className="h-7 gap-1.5 text-xs">
            <a href={o.address_maps_url} target="_blank" rel="noopener noreferrer"><MapPin className="h-3.5 w-3.5" />Abrir no mapa</a>
          </Button>
        ) : undefined}>
        {hasLocation ? (
          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            {address && <div className="sm:col-span-2"><Info label="Endereço Completo">{address}</Info></div>}
            {o.address_reference && <Info label="Ponto de Referência">{o.address_reference}</Info>}
            {(o.address_phone || o.address_contact_name) && (
              <Info label="Contato no Endereço">
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {o.address_contact_name && <span className="font-medium">{o.address_contact_name}</span>}
                  {o.address_phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{o.address_phone}</span>}
                </span>
              </Info>
            )}
            {coords && <Info label="Coordenadas do Local">{coords}</Info>}
          </div>
        ) : <p className="text-sm text-muted-foreground">Nenhum endereço informado.</p>}
      </DetailsContentCard>

      {arrival && (
        <DetailsContentCard icon={LocateFixed} title="Chegada ao Local">
          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <Info label="Horário">{new Date(arrival.at).toLocaleString('pt-BR')}</Info>
            <Info label="Situação">
              {arrival.data.within_radius == null
                ? <span className="text-muted-foreground">Sem coordenadas de referência</span>
                : arrival.data.within_radius
                  ? <span className="font-medium text-success">Dentro da área</span>
                  : <span className="font-medium text-warning">Fora da área</span>}
            </Info>
            {arrival.data.distance_m != null && <Info label="Distância do Ponto da OS">{Math.round(arrival.data.distance_m)} m (raio {arrival.data.radius_m} m)</Info>}
            {arrival.data.accuracy != null && <Info label="Precisão do GPS">±{Math.round(arrival.data.accuracy)} m</Info>}
            {arrival.data.justification && <div className="sm:col-span-2"><Info label="Justificativa">{arrival.data.justification}</Info></div>}
            <Info label="Foto da Fachada">{arrival.data.has_photo ? 'Anexada no andamento de chegada' : 'Não registrada'}</Info>
          </div>
        </DetailsContentCard>
      )}

      {mobileSidebar && <div className="border-y py-2">{mobileSidebar}</div>}
    </div>
  );
}
